import { PageSizeType } from "@/app/model/types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MonitoringFiltersRequest,
  MonitoringItemsResponse,
  MonitoringSliceState,
} from "./type";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";

const initialState: MonitoringSliceState = {
  items: [],
  filters: null,
  usedFilters: {},
  error: null,
  isLoading: false,
  isMiniLoading: false,
  pageSize: (+localStorage.getItem("psizeMon") as PageSizeType) || 20,
  totalCount: 0,
  allTotalCount: 0,
  successCount: 0,
  errorCount: 0,
  successRate: 0,
};

export const fetchMonitoringItems = createAsyncThunk(
  "monitoring/exchange-error-logs",
  async (requestBody: MonitoringFiltersRequest | "", { rejectWithValue }) => {
    let body = {
      pagination: {
        pageSize: +localStorage.getItem("psizeMon") || 20,
        pageNumber: 1,
      },
      filters: [] as object[],
      order: {
        type: "desc",
        field: "Created",
      },
    };

    if (requestBody) {
      const { filters, pagination, order } = requestBody;

      body = {
        pagination,
        filters,
        order,
      };
    }
    try {
      const response = await $api.post<MonitoringItemsResponse>(
        "/admin/exchange/error",
        body,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchMonitoringFilters = createAsyncThunk(
  "monitoring/filters",
  async (requestBody: MonitoringFiltersRequest | "", { rejectWithValue }) => {
    let body = {
      filters: [] as object[],
    };

    if (requestBody) {
      const { filters } = requestBody;

      body = {
        filters,
      };
    }
    try {
      const response = await $api.post<{
        filters: { fields?: Record<string, string[]> }[];
      }>("/filters/exchange", body);
      return response.data?.filters[0]?.fields || {};
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    setUsedFilters(state, action: PayloadAction<typeof state.usedFilters>) {
      state.usedFilters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMonitoringItems.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchMonitoringItems.fulfilled,
      (state, action: PayloadAction<MonitoringItemsResponse>) => {
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.allTotalCount = action.payload.allTotalCount ?? 0;
        state.successCount = action.payload.successCount ?? 0;
        state.errorCount = state.allTotalCount - state.successCount;
        state.successRate = action.payload.successRate ?? 0;
        state.isLoading = false;
      },
    );
    builder.addCase(fetchMonitoringItems.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchMonitoringFilters.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchMonitoringFilters.fulfilled,
      (state, action: PayloadAction<Record<string, string[]>>) => {
        state.filters = action.payload;
        state.isMiniLoading = false;
      },
    );
    builder.addCase(fetchMonitoringFilters.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });
  },
});
export const { setPageSize, setUsedFilters } = monitoringSlice.actions;
